import {BaseElement, BaseElementAsync, OSkinableInit} from "/@back@/commons/basis.js";
import {IItemUiEnv, newSrcView} from "/@back@/wsp/views/itemMain.js";
import {IReg, REG} from "/@lib@/commons/registry.js";
import {IView, IViewApi, IViewsContainer, OViewVisitOptions, VIEWS} from "/@lib@/commons/views.js";
import {JSX} from "/@lib@/commons/xml/dom.js";
import {IDatasTagView} from "/@lib@/wsp/wspMetaUi.js";

/**
 * Mise en page d'une view des tasks.
 *
 * Config attributes:
 */
export interface TaskLayout extends BaseElement {
	initialize(init: OTaskLayoutInit): this;
}

export interface OTaskLayoutInit extends OSkinableInit {
	reg?: IReg<IItemUiEnv>
	bodyView: IDatasTagView[]
}

export class TaskLayout extends BaseElementAsync implements IViewsContainer {
	reg: IReg<IItemUiEnv>;

	async _initialize(init: OTaskLayoutInit) {
		this.reg = this.findReg(init);
		this._attach(this.localName, init);
		let sr = this.shadowRoot;
		this.reg.installSkin("scroll/large", sr);
		for (const v of init.bodyView) {
			await this.buildSrcView(v, this.shadowRoot);
		}
	}

	async buildSrcView(v: IDatasTagView, parent: Node): Promise<IView | null> {
		if (!v) return null;
		if (typeof v === 'string') parent.appendChild(document.createTextNode(v));
		else {
			const tag = await newSrcView(v, this.reg);
			if (!tag) return null;
			parent.appendChild(tag);
			if (v.children) for (const subV of v.children) {
				if (typeof subV === 'string') tag.appendChild(document.createTextNode(subV));
				else await this.buildSrcView(subV, tag);
			}
			return tag;
		}
	}

	visitViews(visitor: (view: IViewApi) => any, options?: OViewVisitOptions): any {
		return VIEWS.visitDescendants(this.shadowRoot, visitor);
	}

	async visitViewsAsync(visitor: (view: IViewApi) => Promise<any>, options?: OViewVisitOptions): Promise<any> {
		return VIEWS.visitDescendantsAsync(this.shadowRoot, visitor);
	}
}

REG.reg.registerSkin("wsp-task-layout", 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
	}
`);

REG.reg.registerSkin("wsp-task-layout/edit", 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: grid;
		grid-template-areas: "title title" "bar body";
		grid-template-columns: auto 1fr;
		grid-template-rows: auto 1fr;
		height: 100%;
	}

  [hidden] {
	  display: none;
  }

  #body {
	  grid-area: body;
	  overflow: auto;
	  padding: .3em;
  }

  #title {
	  grid-area: title;
	  border: 1px solid transparent;
	  border-bottom: 3px solid var(--alt1-bgcolor);
  }

  #bar {
	  grid-area: bar;
	  display: flex;
	  min-height: 0;
	  min-width: 1.8rem;
	  flex-direction: column;
	  background-color: var(--alt1-bgcolor);
  }

  h1 {
	  color: var(--alt1-color);
	  font-size: 1.2em;
	  text-align: center;
	  margin: 0;
	  padding: .3em;
  }

  .form {
	  display: grid;
	  grid-template-columns: auto 1fr;
	  grid-template-rows: auto;
	  grid-gap: .7em;
	  margin-inline-start: .5em;
  }

  .form > wsp-task-field-lc {
	  width: max-content;
  }

  label {
	  color: var(--alt1-color);
	  align-self: center;
	  white-space: nowrap;
  }

  label.top {
	  align-self: start;
  }

  .oneCol {
	  grid-column-start: 1;
	  grid-column-end: 3;
  }
`);

customElements.define("wsp-task-layout", TaskLayout);